/**
 * Genemo ファミリー共通の計測（GA4・Microsoft Clarity）。全アプリに同じ内容でコピーする（アプリ側で編集しない）。
 * 使い方: main.tsx で initAnalytics()、ルートが変わるたびに trackPage(pathname)、出来事は track(name, params)。
 *
 * - 送るのは genemo.jp / *.genemo.jp で動いているときだけ（手元の開発環境からは送らない）
 * - ブラウザのトラッキング拒否（Do Not Track / Global Privacy Control）が有効なら何も読み込まない
 * - URL はクエリ・ハッシュを落とし、ID らしい部分を :id に置き換えてから送る（トークン・作品 ID・題名を送らない）
 * - Clarity（操作の録画）はログイン前の公開ページだけで、読み込んだ最初のページに限る。SPA で URL が変わる直前
 *   （戻る操作を含む）と setRecording(false) で止め、そのページを読み込み直すまで再開しない
 * - OAuth（Google・ラクリプ）での登録・ログインは、サーバーが置く目印の Cookie（genemo_auth）で知る。
 *   戻り先がどのアプリでも 1 回だけ送る（メールでの登録・ログインは各画面が track() を直接送る）
 *
 * 外部送信の公表は short.genemo.jp/privacy#analytics。送る内容を変えたらポリシーも直す。
 * genemo.jp の LP 側は g002genemo_home/docs/lp/product-lps/site/shared/analytics.js（同じ ID・同じ方針）。
 */
const GA4_ID = 'G-PFBSKMC2WZ'
const CLARITY_ID = 'yv2wrnpq6w'
const AUTH_MARK = 'genemo_auth'

type Params = Record<string, string | number | boolean | undefined | Array<Record<string, string | number>>>
type Fn = (...args: unknown[]) => void
const w = window as Window & { dataLayer?: unknown[]; gtag?: Fn; clarity?: Fn & { q?: unknown[] } }

let on = false
let recording: 'off' | 'on' | 'stopped' = 'off'
let lastPath = ''
let lastLocation = ''

function familyHost(host: string): boolean {
  return host === 'genemo.jp' || host.endsWith('.genemo.jp')
}

/** /project/prj_Ab12/render → /project/:id/render（小文字の英単語とハイフンだけの部分を残す） */
export function cleanPath(pathname: string): string {
  const p = pathname.split('/').map(s => (s === '' || /^[a-z]+(?:-[a-z]+)*$/.test(s) && s.length <= 24 ? s : ':id')).join('/')
  return p.replace(/\/+$/, '') || '/'
}

function cleanReferrer(): string {
  try {
    const u = new URL(document.referrer)
    return u.origin + (familyHost(u.hostname) ? cleanPath(u.pathname) : '/')   // 外のサイトは入口のドメインだけ
  } catch { return '' }
}

/** サーバー（Short の OAuth コールバック）が置いた目印を読んで消す。形は signup-google / login-rakurip */
function readAuthMark(): { kind: 'sign_up' | 'login'; method: string } | null {
  const m = document.cookie.match(/(?:^|;\s*)genemo_auth=(signup|login)-([a-z]+)/)
  if (!m) return null
  // サーバーは本番で .genemo.jp に置くが、ドメイン指定なしで置かれた場合も消えるよう両方で消す
  document.cookie = `${AUTH_MARK}=; Max-Age=0; path=/; domain=.genemo.jp; secure; samesite=lax`
  document.cookie = `${AUTH_MARK}=; Max-Age=0; path=/; secure; samesite=lax`
  return { kind: m[1] === 'signup' ? 'sign_up' : 'login', method: m[2] }
}

export function initAnalytics(): void {
  if (on || !familyHost(location.hostname)) return
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean }
  if (nav.globalPrivacyControl === true || nav.doNotTrack === '1' || (window as { doNotTrack?: string }).doNotTrack === '1') return
  on = true
  const s = document.createElement('script')
  s.async = true
  s.src = 'https://www.googletagmanager.com/gtag/js?id=' + GA4_ID
  document.head.appendChild(s)
  w.dataLayer = w.dataLayer || []
  // gtag は arguments オブジェクトのまま積む必要がある
  w.gtag = function () { w.dataLayer!.push(arguments) } // eslint-disable-line prefer-rest-params
  w.gtag('js', new Date())
  w.gtag('set', { page_title: 'Genemo' })   // 画面の題名に作品名が入ることがあるので固定
  w.gtag('config', GA4_ID, { send_page_view: false })   // ページ閲覧は trackPage で URL を整えてから送る
}

/** ルートが変わるたびに呼ぶ。同じパスが続けて来たら（StrictMode の二重実行など）送らない */
export function trackPage(pathname: string): void {
  if (!on || pathname === lastPath) return
  lastPath = pathname
  const loc = location.origin + cleanPath(pathname)
  w.gtag!('set', { page_location: loc, page_referrer: lastLocation || cleanReferrer() })   // 以降のイベントもこの URL で送られる
  lastLocation = loc
  w.gtag!('event', 'page_view')
  const mark = readAuthMark()
  if (mark) track(mark.kind, { method: mark.method })
}

export function track(name: string, params: Params = {}): void {
  if (on) w.gtag!('event', name, params)
}

const sent = new Set<string>()
/** 同じ key では（そのページを開いている間）1 回だけ送る。ジョブの完了を複数の場所で検知する画面用。key 自体は送らない */
export function trackOnce(key: string, name: string, params: Params = {}): void {
  if (sent.has(key)) return
  sent.add(key)
  track(name, params)
}

function stopRecording(): void {
  if (recording !== 'on') return
  recording = 'stopped'
  w.clarity!('stop')
}

/**
 * Clarity の録画を、ログイン前の公開ページを読み込んだときだけ動かす（allowed はいまの画面で録画してよいか）。
 * SPA で URL（パスかクエリ）が変わるときは、行き先がどこでも、URL が変わる前に止める。Clarity は URL が変わると
 * 約 250ms 後に自分で録画を再開する予約を入れ、あとから止めてもその予約は取り消せないため、予約自体をさせない。
 * ログインの切り替わりは、呼び出し側が useLayoutEffect で allowed=false を渡して描画の前に止める。
 */
export function setRecording(allowed: boolean): void {
  if (!on || recording === 'stopped') return
  if (!allowed) { stopRecording(); return }
  if (recording === 'on') return
  recording = 'on'
  // Clarity より先に差し込む（Clarity は読み込み後に pushState を包んで元の処理を先に呼び、戻る操作も後から聞くので、
  // こちらが先に止められる。止まっていれば Clarity は再開の予約をしない）
  for (const m of ['pushState', 'replaceState'] as const) {
    const orig = history[m]
    history[m] = function (this: History, data: unknown, unused: string, url?: string | URL | null) {
      if (url != null) {
        const u = new URL(String(url), location.href)
        if (u.pathname + u.search !== location.pathname + location.search) stopRecording()
      }
      return orig.call(this, data, unused, url)
    }
  }
  window.addEventListener('popstate', stopRecording)
  // Clarity 公式の読み込み前の受け皿と同じ（呼び出しを arguments のまま積んでおく）
  w.clarity = w.clarity || function () { (w.clarity!.q = w.clarity!.q || []).push(arguments) } // eslint-disable-line prefer-rest-params
  const s = document.createElement('script')
  s.async = true
  s.src = 'https://www.clarity.ms/tag/' + CLARITY_ID
  document.head.appendChild(s)
}
